import { type JSX } from "react";

import { WholePageSpinner } from "@/src/components/atoms";

import EmailVerificationDialog from "@/src/auth/components/EmailVerificationDialog";
import { useAuth } from "@/src/auth/hooks/useAuth";
import { usePublicRouteRedirect } from "@/src/auth/hooks/usePublicRouteRedirect";

interface PublicRouteProps {
  children: JSX.Element;
  routeType: "consumer" | "dealer" | "admin";
  /**
   * Render children while authentication is still resolving instead of a
   * spinner. Required for prerendered routes: auth always starts as
   * "authenticating", so without this the static HTML (and the first client
   * render it hydrates against) would only contain the spinner.
   */
  renderWhileAuthenticating?: boolean;
}

export const PublicRoute = (props: PublicRouteProps) => {
  const { isAuthenticating } = useAuth();

  // redirect behaviors are handled in this hook
  const { redirectNeeded } = usePublicRouteRedirect({
    routeType: props.routeType
  });

  const waitForAuth = isAuthenticating && !props.renderWhileAuthenticating;

  // Show spinner while authentication completes
  if (waitForAuth || redirectNeeded) {
    return <WholePageSpinner />;
  }

  // Once authentication is complete, render the public route
  return (
    <>
      <EmailVerificationDialog /> {/* force users to verify their email */}
      {props.children}
    </>
  );
};
